import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Cookies, withCookies } from 'react-cookie';

import moment from 'moment';
import { connect } from 'react-redux';
import { observer } from 'mobx-react';

// core
import { makeFetch } from '_core/utils/makeFetch';
import { momentLocales } from '_core/utils/momentLocales';
import { findInObject } from '_core/utils/findInObject';
import { encodeKey, getValue, setValue } from '_core/utils/storage';
import { withVisitorInfo } from '_core/utils/visitorInfo';
import { withLang } from '_core/hocs/withLang';
import { withContext } from '_core/hocs/withContext';
import { GlobalContext } from '_core/hooks/useGlobal';
import { RootStoreContext } from '_core/storeMobx/RootStore';
import { privateDataActions } from '_core/store/PrivateData';
import { langActions } from '_core/store/Lang';
import { themeActions } from '_core/store/Theme';
import { countryActions } from '_core/store/Country';
import { currencyActions } from '_core/store/Currency';

import { RootView } from './RootView';


const mapDispatch = {
  resetPrivateData: privateDataActions.resetPrivateData,
  updateLangList: langActions.updateLangList,
  updateLangStrings: langActions.updateLangStrings,
  updateCountry: countryActions.updateCountry,
  updateCurrency: currencyActions.updateCurrency,
  changeCurrency: currencyActions.changeCurrency,
  updateTheme: themeActions.updateTheme,
}
@observer
class RawRoot extends PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      isGuestReady: false,
      isPrivateReady: false,
    }

    this._isMounted = false;
    this._rolesTimeout = null;

    this._isReady = this._isReady.bind(this)
    this._getRoles = this._getRoles.bind(this)
    this._publicInit = this._publicInit.bind(this)
    this._privateInit = this._privateInit.bind(this)
    this._onLangUpdate = this._onLangUpdate.bind(this)
    this._onRolesUpdate = this._onRolesUpdate.bind(this)
    this._setCurrency = this._setCurrency.bind(this)
    this._fetchTheme = this._fetchTheme.bind(this)
    this._setTheme = this._setTheme.bind(this)
  }

  async componentDidMount() {
    const {
      request: { onNotice },
      config: { API_NOTICE }
    } = this.props;
    
    this._isMounted = true;

    this._getRoles(true);
    this._publicInit();
    
    this._onNoticeDispose = onNotice([API_NOTICE.VISITOR_STATUS, API_NOTICE.VISITOR_LOGOUT], () => {
      this._clearRolesTimeout();
      this._getRoles(false);
    });
  }

  componentWillUnmount() {
    this._onNoticeDispose();
    this._clearRolesTimeout();
    
    this._isMounted = false
  }

  componentDidUpdate(...args) {
    // Handle lang change
    this._onLangUpdate(...args)

    // Handle auth
    this._onRolesUpdate(...args)
  }

  _setCurrency(result) {
    const {
      cookies,
      updateCurrency,
      config: { COOKIES_KEY },
    } = this.props

    const currency = cookies.get(encodeKey(COOKIES_KEY.CURRENCY))

    if (
      result == null ||
      !Array.isArray(result.list) ||
      result.list.length === 0
    ) {
      updateCurrency({ list: [], current: null })
      return
    }

    if (result.list.some(item => `${item.id}` === `${currency}`)) {
      result.current = +currency
    } else {
      result.current = result.current.id
      const stamp = Date.now() + 10 * 365 * 24 * 60 * 60 * 1000

      cookies.set(encodeKey(COOKIES_KEY.CURRENCY), result.current, {
        path: '/',
        expires: new Date(stamp),
      })
    }

    updateCurrency(result)
  }

  async _setTheme(result) {
    const {
      cookies,
      updateTheme,
      config: { COOKIES_KEY },
    } = this.props

    const theme = cookies.get(encodeKey(COOKIES_KEY.THEME))

    if (
      result == null ||
      !Array.isArray(result.list) ||
      result.list.length === 0
    ) {
      updateTheme({ list: [], current: null })
      return
    }

    if (result.list.some(item => `${item.id}` === `${theme}`)) {
      result.current = +theme
    } else {
      result.current = result.list[0].id
      const stamp = Date.now() + 10 * 365 * 24 * 60 * 60 * 1000

      cookies.set(encodeKey(COOKIES_KEY.THEME), result.current, {
        path: '/',
        expires: new Date(stamp),
      })
    }

    updateTheme(result)
    await this._fetchTheme(result)
  }

  async _fetchTheme(data) {
    const {
      theming: { applyTheme },
    } = this.props

    const themeData = findInObject(data.list, 'id', data.current)

    try {
      applyTheme(await makeFetch({ url: themeData.path }))
    } catch (error) {
      process.env.NODE_ENV !== 'production' && console.error(error)
    }
  }

  async _onLangUpdate(prevProps) {
    const {
      langInfo,
      config: { DEFAULT_MOMENT_LOCALE },
    } = this.props

    // Set correct `moment` locale on startup
    if (prevProps.langInfo == null && langInfo != null) {
      const code = langInfo.code.toLowerCase()
      momentLocales.includes(code) && (await import(`moment/locale/${code}`))
      moment.locale([code, DEFAULT_MOMENT_LOCALE])
    }
  }

  _onRolesUpdate(prevProps) {
    const {
      visitorInfo,
      resetPrivateData,
    } = this.props

    this._setRolesTimeout();

    if (prevProps.visitorInfo.update !== visitorInfo.update) {
      this._getRoles(true);
      
      return;
    }

    // Handle auth data update
    if (!prevProps.visitorInfo.isAuthorized() && visitorInfo.isAuthorized()) {
      this._privateInit()
    }

    // Handle auth data reset
    if (prevProps.visitorInfo.isAuthorized() && !visitorInfo.isAuthorized()) {
      resetPrivateData()
      this._getRoles(true)
    }
  }
  
  _setRolesTimeout() {
    this._clearRolesTimeout();
    
    const {
      request,
      config: { VISITOR_UPDATE_INTERVAL },
    } = this.props;
    
    this._rolesTimeout = setTimeout(() => {
      const {
        visitorInfo
      } = this.props
      
      if (!visitorInfo.isAuthorized())
        return;
      
      this._setRolesTimeout();
      
      if (request.isNoticeActive)
        this._getRoles(false);
    }, VISITOR_UPDATE_INTERVAL);
  }
  
  _clearRolesTimeout() {
    if (this._rolesTimeout)
      clearTimeout(this._rolesTimeout);
    
    this._rolesTimeout = null;
  }

  _getRoles(loading) {
    const {
      request: { sendAndDo },
      config: { API_METHOD, STORAGE_KEY },
      visitorInfo
    } = this.props;

    loading && this.setState({ visitorLoading: true });

    sendAndDo({
      method: API_METHOD.VISITOR_INFO,
      onSuccess: (result) => {
        visitorInfo.set(result);
        
        if (getValue(STORAGE_KEY.UNLOCK))
          setValue(STORAGE_KEY.UNLOCK, { login: result.login, firstname: result.firstname });
      },
      onFailure: () => visitorInfo.set(null),
      onResult: () => loading && this.setState({ visitorLoading: false })
    })
  }

  _getCart() {
    const {
      config: { REPLACE_CURRENCY },
      cartStore
    } = this.props;
    
    cartStore.setReplaceCurrency(REPLACE_CURRENCY.SHOP_CART);
    cartStore.fetch();
  }


  async _publicInit() {
    const {
      updateLangList,
      updateLangStrings,
      i18n: { guest },
      request: { sendAndDo },
      config: { API_METHOD },
      currencyStore,
    } = this.props

    let isGuestReady = true

    await Promise.all([
      sendAndDo({
        method: API_METHOD.THEME,
        onSuccess: this._setTheme,
        onFailure: () => (isGuestReady = false),
        waitResult: true,
      }),
      sendAndDo({
        method: API_METHOD.LANG_LIST,
        onSuccess: updateLangList,
        onFailure: () => (isGuestReady = false),
        waitResult: true,
      }),
      sendAndDo({
        method: API_METHOD.LANG_STRINGS,
        params: { list: guest },
        onSuccess: updateLangStrings,
        onFailure: () => (isGuestReady = false),
      }),
    ])

    this.forceUpdate()

    await Promise.all([
      sendAndDo({
        method: API_METHOD.CURRENCY,
        onSuccess: result => {
          currencyStore.init(result)
          this._setCurrency(result)
        },

        // onFailure: () => (isGuestReady = false),
      }),
    ])

    if (!this._isMounted) {
      return
    }

    // TODO: find better solution and remove
    await sendAndDo({
      method: API_METHOD.LANG_STRINGS,
      params: { list: guest },
      onSuccess: updateLangStrings,
      onFailure: () => (isGuestReady = false),
    })

    this.setState({ isGuestReady })
  }

  async _privateInit() {
    const {
      updateLangStrings,
      i18n: { client },
      config: { API_METHOD, HAS_CART },
      request: { sendAndDo },
    } = this.props

    let isPrivateReady = true

    await sendAndDo({
      method: API_METHOD.LANG_STRINGS,
      params: { list: client },
      onSuccess: updateLangStrings,
      onFailure: () => (isPrivateReady = false),
    })

    this.forceUpdate()

    if (!this._isMounted) {
      return
    }

    // TODO: find better solution and remove
    await sendAndDo({
      method: API_METHOD.LANG_STRINGS,
      params: { list: client },
      onSuccess: updateLangStrings,
      onFailure: () => (isPrivateReady = false),
    })

    await this.setState({ isPrivateReady })

    HAS_CART && this._getCart()
  }

  _isReady() {
    const { visitorInfo } = this.props;
    
    const { isGuestReady, isPrivateReady } = this.state;
    
    if (this.state.visitorLoading)
      return false;
    
    const isAuthorized = visitorInfo.isAuthorized();
    
    return !isAuthorized && isGuestReady
      || isAuthorized && isPrivateReady;
  }

  render() {
    const { visitorInfo } = this.props;

    const viewProps = {
      isGuest: !visitorInfo.isAuthorized(),
      isReady: this._isReady()
    };

    return <RootView {...viewProps} />;
  }
};

RawRoot.defaultProps = {};

RawRoot.propTypes = {
  // `redux-connect` HOC props
  resetPrivateData: PropTypes.func.isRequired,
  updateLangList: PropTypes.func.isRequired,
  updateLangStrings: PropTypes.func.isRequired,
  updateCountry: PropTypes.func.isRequired,
  updateCurrency: PropTypes.func.isRequired,
  changeCurrency: PropTypes.func.isRequired,

  // `withCookies` HOC props
  cookies: PropTypes.instanceOf(Cookies),

  // `GlobalContext` props
  request: PropTypes.object.isRequired,

  // `RootStoreContext` props
  cartStore: PropTypes.object.isRequired,

  // withVisitorInfo
  visitorInfo: PropTypes.object.isRequired,

  // `withLang` HOC props
  langInfo: PropTypes.object,
  langStrings: PropTypes.object,
  getLangObject: PropTypes.func,
  getFirstLangString: PropTypes.func,
  getLangStringSet: PropTypes.func,
  getLangString: PropTypes.func,
};

export const Root = withContext(RootStoreContext)(
  withContext(GlobalContext)(
    withVisitorInfo(withLang(withCookies(connect((state) => ({}), mapDispatch)(RawRoot))))
  )
);
